import { Injectable } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ContextEnum } from '@shared/enums/context.enum';
import { CustomHttpError } from '@shared/interfaces/custom-http-error/custom-http-error.interface';
import { ProductManager } from '@shared/interfaces/product-manager/product-manager.interface';
import { ProductManagersService } from '@shared/service/product-manager/product-managers.service';
import { applicationActions } from '@store/application/application.actions';
import { catchError, map, of, switchMap } from 'rxjs';

import { productManager } from './product-managers.actions';

@Injectable()
export class ProductManagersEffects {
  loadProductManagers = createEffect(() =>
    this.actions$.pipe(
      ofType(productManager.load),
      switchMap((payload: { searchInput?: string }) =>
        this.productManagersService.getProductManagers(payload.searchInput).pipe(
          map((response: Array<ProductManager>) =>
            productManager.loadSuccess({ productManagers: response })
          ),
          catchError((error: CustomHttpError) =>
            of(applicationActions.genericError({ message: error.error.details }))
          )
        )
      )
    )
  );

  createProductManager = createEffect(() =>
    this.actions$.pipe(
      ofType(productManager.create),
      switchMap((payload: { productManager: Omit<ProductManager, 'id'> }) =>
        this.productManagersService.postProductManager(payload.productManager).pipe(
          map(() => {
            productManager.saveSuccess({ context: ContextEnum.CREATE });
            return applicationActions.genericSuccess({
              message: this.translocoService.translate('productManagers.store.createSuccess')
            });
          }),
          catchError((error: CustomHttpError) => {
            productManager.saveFailure({ context: ContextEnum.CREATE });
            return of(applicationActions.genericError({ message: error.error.details }));
          })
        )
      )
    )
  );

  updateProductManager = createEffect(() =>
    this.actions$.pipe(
      ofType(productManager.update),
      switchMap((payload: { productManager: ProductManager }) =>
        this.productManagersService.putProductManager(payload.productManager).pipe(
          map(() => {
            productManager.saveSuccess({ context: ContextEnum.UPDATE });
            return applicationActions.genericSuccess({
              message: this.translocoService.translate('productManagers.store.updateSuccess')
            });
          }),
          catchError((error: CustomHttpError) => {
            productManager.saveFailure({ context: ContextEnum.UPDATE });
            return of(applicationActions.genericError({ message: error.error.details }));
          })
        )
      )
    )
  );

  deleteProductManagers = createEffect(() =>
    this.actions$.pipe(
      ofType(productManager.delete),
      switchMap((payload: { id: number; searchInput: string; name: string }) =>
        this.productManagersService.deleteProductManagerById(payload.id).pipe(
          map(() => {
            applicationActions.genericSuccess({ message: `Deleted - ${payload.name}` });
            return productManager.load({ searchInput: payload.searchInput });
          }),
          catchError((error: CustomHttpError) =>
            of(applicationActions.genericError({ message: error.error.details }))
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly productManagersService: ProductManagersService,
    private readonly translocoService: TranslocoService
  ) {}
}
